import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, Circle, PartyPopper, ChevronRight, X, Sparkles, Image, Phone, Users, Bell, Wallet, QrCode } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface OnboardingChecklistProps {
    stats: {
        totalClients: number;
        hasMembershipPlans: boolean;
        todayCheckIns: number;
        totalCheckIns?: number;
        staffCount?: number;
    };
    gymId?: string;
    gymSettings?: {
        logo?: string;
        phone?: string;
        country?: string;
        city?: string;
        modules_enabled?: string[];
    };
}

const isPushEnabled = () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return false;
    return Notification.permission === 'granted';
};

const ALL_TASKS = [
    {
        id: 'plans',
        title: 'Configura tus Planes',
        description: 'Define las membresías que ofrecerás.',
        icon: Sparkles,
        completedCheck: (stats: any) => stats.hasMembershipPlans,
        action: () => '/settings?tab=membresias',
    },
    {
        id: 'clients',
        title: 'Registra un Cliente',
        description: 'Añade a tu primer socio al sistema.',
        icon: Users,
        completedCheck: (stats: any) => stats.totalClients > 0,
        action: () => '/clients',
    },
    {
        id: 'checkin',
        title: 'Realiza un Check-in',
        description: 'Prueba el escáner con un cliente.',
        icon: QrCode,
        completedCheck: (stats: any) => (stats.totalCheckIns ?? stats.todayCheckIns) > 0,
        action: () => '/scanner',
    },
    {
        id: 'profile',
        title: 'Completa tu Perfil',
        description: 'Sube logo y datos de contacto.',
        icon: Image,
        completedCheck: (_stats: any, settings?: any) => !!(settings?.phone || settings?.logo),
        action: () => '/settings?tab=general',
    },
    {
        id: 'staff',
        title: 'Invita a tu Staff',
        description: 'Agrega instructores o recepcionistas.',
        icon: Bell,
        completedCheck: (stats: any) => (stats.staffCount ?? 0) > 0,
        action: () => '/settings?tab=staff',
        optional: true,
    },
    {
        id: 'push',
        title: 'Activa Notificaciones',
        description: 'Recibe alertas de miembros y pagos.',
        icon: Bell,
        completedCheck: () => isPushEnabled(),
        action: () => '/settings?tab=notificaciones',
        optional: true,
    },
    {
        id: 'cashbox',
        title: 'Configura Caja Chica',
        description: 'Controla ingresos y egresos diarios.',
        icon: Wallet,
        completedCheck: (_stats: any) => false,
        action: () => '/sales',
        optional: true,
    },
];

export function OnboardingChecklist({ stats, gymId, gymSettings }: OnboardingChecklistProps) {
    const STORAGE_KEY = `gympro_onboarding_dismissed_${gymId}`;
    const PROGRESS_KEY = `gympro_onboarding_progress_${gymId}`;
    const [isOpen, setIsOpen] = useState(localStorage.getItem(STORAGE_KEY) !== 'true');
    const [isVisible, setIsVisible] = useState(false);
    const [manuallyCompleted, setManuallyCompleted] = useState<string[]>(() => {
        try {
            const saved = localStorage.getItem(PROGRESS_KEY);
            return saved ? JSON.parse(saved) : [];
        } catch { return []; }
    });
    const navigate = useNavigate();

    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => setIsVisible(true), 1000);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    const handleDismiss = (permanent = false) => {
        setIsOpen(false);
        if (permanent || isFullyCompleted) {
            localStorage.setItem(STORAGE_KEY, 'true');
        }
    };

    // Determine which tasks are completed
    const tasks = ALL_TASKS.map(task => {
        const completedByStats = task.completedCheck(stats, gymSettings);
        const completedManually = manuallyCompleted.includes(task.id);
        return {
            ...task,
            completed: completedByStats || completedManually,
        };
    });

    // Only count main (non-optional) tasks for progress
    const mainTasks = tasks.filter(t => !t.optional);
    const completedCount = mainTasks.filter(t => t.completed).length;
    const progressPercent = mainTasks.length > 0 ? (completedCount / mainTasks.length) * 100 : 0;
    const isFullyCompleted = mainTasks.length > 0 && completedCount === mainTasks.length;

    // Auto-dismiss if fully completed
    useEffect(() => {
        if (isFullyCompleted && isOpen) {
            const timer = setTimeout(() => {
                setIsOpen(false);
                localStorage.setItem(STORAGE_KEY, 'true');
            }, 8000);
            return () => clearTimeout(timer);
        }
    }, [isFullyCompleted, isOpen]);

    // Save manual completions
    const markCompleted = (taskId: string) => {
        const updated = [...manuallyCompleted, taskId];
        setManuallyCompleted(updated);
        localStorage.setItem(PROGRESS_KEY, JSON.stringify(updated));
    };

    const handleAction = (task: typeof tasks[0]) => {
        if (task.completed) return;
        const path = task.action();
        navigate(path);
    };

    if (!isOpen) return null;

    return (
        <div
            className={cn(
                "fixed bottom-6 right-6 z-50 transition-all duration-700 transform",
                isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"
            )}
        >
            <Card className="w-80 shadow-2xl border-primary/20 bg-slate-900/95 backdrop-blur-xl text-white overflow-hidden ring-1 ring-white/10">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-amber-400" />
                <CardHeader className="pb-2 pt-4 px-4 flex flex-row items-center justify-between space-y-0">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-primary/20 text-primary">
                            <Sparkles className="h-4 w-4" />
                        </div>
                        <CardTitle className="text-sm font-bold">Primeros Pasos</CardTitle>
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-slate-400 hover:text-white hover:bg-white/10 rounded-full"
                        onClick={() => handleDismiss(false)}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                    <div className="space-y-1.5">
                        <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider text-slate-400">
                            <span>Progreso {isFullyCompleted ? '🎉' : ''}</span>
                            <span>{completedCount} de {mainTasks.length}</span>
                        </div>
                        <Progress value={progressPercent} className="h-1.5 bg-slate-800 [&>div]:bg-gradient-to-r [&>div]:from-orange-500 [&>div]:to-amber-400" />
                    </div>

                    <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1 custom-scrollbar">
                        {tasks.map((task) => (
                            <div
                                key={task.id}
                                onClick={() => handleAction(task)}
                                className={cn(
                                    "group p-3 rounded-xl border transition-all cursor-pointer flex items-center gap-3",
                                    task.completed
                                        ? "bg-green-500/10 border-green-500/20 opacity-70"
                                        : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-primary/50"
                                )}
                            >
                                <div className={cn(
                                    "shrink-0 transition-transform group-hover:scale-110",
                                    task.completed ? "text-green-500" : "text-primary"
                                )}>
                                    {task.completed ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className={cn(
                                        "text-xs font-bold",
                                        task.completed ? "text-green-500 line-through opacity-70" : "text-white"
                                    )}>
                                        {task.title}
                                    </p>
                                    <p className="text-[10px] text-slate-400 truncate">{task.description}</p>
                                </div>
                                {!task.completed && (
                                    <ChevronRight className="h-4 w-4 text-slate-600 transition-transform group-hover:translate-x-1" />
                                )}
                            </div>
                        ))}
                    </div>

                    {isFullyCompleted ? (
                        <div className="p-3 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white flex items-center gap-3 animate-in zoom-in duration-500 shadow-lg shadow-green-600/30">
                            <PartyPopper className="h-5 w-5" />
                            <div className="flex-1">
                                <p className="text-xs font-bold">¡Todo listo!</p>
                                <p className="text-[10px] opacity-90">Has configurado lo esencial.</p>
                            </div>
                            <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-[10px] font-bold hover:bg-white/20 text-white"
                                onClick={() => handleDismiss(true)}
                            >
                                CERRAR
                            </Button>
                        </div>
                    ) : (
                        <Button
                            variant="link"
                            className="w-full text-zinc-500 hover:text-zinc-300 text-[10px] font-bold uppercase tracking-widest h-auto p-0"
                            onClick={() => handleDismiss(true)}
                        >
                            Omitir guía para siempre
                        </Button>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
